import { useEffect, useState } from 'react';
import { api } from '../../../api/client';

interface Student {
  id: string;
  firstName: string;
  paternalLastName: string;
  fatherName?: string;
  fatherEmail?: string;
  motherName?: string;
  motherEmail?: string;
}

interface Guardian {
  id: string;
  guardianName: string;
  guardianDocument: string | null;
  relationship: string;
  isAuthorized: boolean;
  validUntil: string | null;
  student: { id: string; firstName: string; paternalLastName: string };
}

export default function SecurityPage() {
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [search, setSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [guardians, setGuardians] = useState<Guardian[]>([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    guardianName: '',
    guardianDocument: '',
    relationship: 'PARENT',
    validUntil: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/students').then((data) => {
      const s = Array.isArray(data) ? data : [];
      setStudents(s);
      setFilteredStudents(s);
      setLoading(false);
    });
  }, []);

  const handleSearch = (q: string) => {
    setSearch(q);
    if (!q.trim()) {
      setFilteredStudents(students);
      return;
    }
    const lower = q.toLowerCase();
    setFilteredStudents(
      students.filter(
        (s) =>
          `${s.firstName} ${s.paternalLastName}`.toLowerCase().includes(lower) ||
          `${s.paternalLastName} ${s.firstName}`.toLowerCase().includes(lower),
      ),
    );
  };

  const selectStudent = async (s: Student) => {
    setSelectedStudent(s);
    setSearch(`${s.firstName} ${s.paternalLastName}`);
    setFilteredStudents([]);
    const data = await api.get('/api/security/guardians', { params: { studentId: s.id } });
    setGuardians(Array.isArray(data) ? data : []);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;
    setSaving(true);
    await api.post('/api/security/guardians', {
      studentId: selectedStudent.id,
      guardianName: form.guardianName,
      guardianDocument: form.guardianDocument,
      relationship: form.relationship,
      validUntil: form.validUntil || null,
    });
    setForm({ guardianName: '', guardianDocument: '', relationship: 'PARENT', validUntil: '' });
    setShowForm(false);
    setSaving(false);
    const data = await api.get('/api/security/guardians', {
      params: { studentId: selectedStudent.id },
    });
    setGuardians(Array.isArray(data) ? data : []);
  };

  const toggleAuth = async (g: Guardian) => {
    await api.put(`/api/security/guardians/${g.id}`, { isAuthorized: !g.isAuthorized });
    setGuardians(
      guardians.map((x) => (x.id === g.id ? { ...x, isAuthorized: !g.isAuthorized } : x)),
    );
  };

  const deleteGuardian = async (id: string) => {
    if (!confirm('¿Eliminar este tutor autorizado?')) return;
    await api.delete(`/api/security/guardians/${id}`);
    setGuardians(guardians.filter((g) => g.id !== id));
  };

  const isExpired = (validUntil: string | null) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('es-MX');

  return (
    <div style={{ maxWidth: 800 }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 4 }}>🔒 Seguridad</h1>
      <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 24 }}>
        Consulta y administra tutores autorizados para recoger alumnos
      </p>

      <div style={{ position: 'relative', marginBottom: 24 }}>
        <input
          type="text"
          placeholder="Buscar alumno por nombre..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          style={{
            width: '100%',
            maxWidth: 400,
            padding: '10px 14px',
            borderRadius: 10,
            border: '2px solid #e0e0e0',
            fontSize: 14,
            outline: 'none',
          }}
        />
        {search && filteredStudents.length > 0 && !selectedStudent && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              maxWidth: 400,
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 10,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 10,
              maxHeight: 200,
              overflowY: 'auto',
            }}
          >
            {filteredStudents.map((s) => (
              <div
                key={s.id}
                onClick={() => selectStudent(s)}
                style={{
                  padding: '10px 14px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: 14,
                }}
              >
                {s.paternalLastName}, {s.firstName}
              </div>
            ))}
          </div>
        )}
      </div>

      {loading && <p style={{ color: '#888' }}>Cargando...</p>}

      {selectedStudent && (
        <>
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a1c2c', margin: 0 }}>
                {selectedStudent.paternalLastName}, {selectedStudent.firstName}
              </h2>
              <button
                onClick={() => {
                  setSelectedStudent(null);
                  setSearch('');
                  setGuardians([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                Limpiar
              </button>
            </div>

            <h3
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#666',
                textTransform: 'uppercase',
                marginBottom: 8,
                letterSpacing: 0.5,
              }}
            >
              Padres / Tutores registrados
            </h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {selectedStudent.fatherName && (
                <div
                  style={{
                    background: '#f0f7ff',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 13,
                  }}
                >
                  <strong style={{ color: '#1a1c2c' }}>{selectedStudent.fatherName}</strong>
                  {selectedStudent.fatherEmail && (
                    <span style={{ color: '#888', marginLeft: 6 }}>
                      ({selectedStudent.fatherEmail})
                    </span>
                  )}
                  <span style={{ color: '#666', marginLeft: 6, fontSize: 11 }}>— Padre</span>
                </div>
              )}
              {selectedStudent.motherName && (
                <div
                  style={{
                    background: '#fef0f7',
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: 13,
                  }}
                >
                  <strong style={{ color: '#1a1c2c' }}>{selectedStudent.motherName}</strong>
                  {selectedStudent.motherEmail && (
                    <span style={{ color: '#888', marginLeft: 6 }}>
                      ({selectedStudent.motherEmail})
                    </span>
                  )}
                  <span style={{ color: '#666', marginLeft: 6, fontSize: 11 }}>— Madre</span>
                </div>
              )}
              {!selectedStudent.fatherName && !selectedStudent.motherName && (
                <span style={{ color: '#bbb', fontSize: 13, fontStyle: 'italic' }}>
                  Sin padres registrados
                </span>
              )}
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a1c2c', margin: 0 }}>
                Autorizados para recoger
              </h2>
              <button
                onClick={() => setShowForm(!showForm)}
                style={{
                  background: 'var(--primary-color)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                + Agregar
              </button>
            </div>

            {showForm && (
              <form
                onSubmit={handleSubmit}
                style={{
                  background: '#f9f9f9',
                  borderRadius: 10,
                  padding: 16,
                  marginBottom: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <input
                  value={form.guardianName}
                  onChange={(e) => setForm({ ...form, guardianName: e.target.value })}
                  placeholder="Nombre completo"
                  required
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    fontSize: 13,
                  }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input
                    value={form.guardianDocument}
                    onChange={(e) => setForm({ ...form, guardianDocument: e.target.value })}
                    placeholder="Documento (opcional)"
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 13,
                    }}
                  />
                  <select
                    value={form.relationship}
                    onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 13,
                    }}
                  >
                    <option value="PARENT">Padre/Madre</option>
                    <option value="GRANDPARENT">Abuelo/a</option>
                    <option value="SIBLING">Hermano/a</option>
                    <option value="OTHER">Otro</option>
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#666',
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    Vigencia hasta
                  </label>
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 13,
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      background: '#fff',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      background: 'var(--primary-color)',
                      color: '#fff',
                      padding: '6px 14px',
                      borderRadius: 8,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {saving ? 'Guardando...' : 'Agregar'}
                  </button>
                </div>
              </form>
            )}

            {guardians.length === 0 ? (
              <p style={{ color: '#bbb', fontSize: 13, fontStyle: 'italic' }}>
                No hay tutores autorizados para este alumno
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {guardians.map((g) => {
                  const expired = isExpired(g.validUntil);
                  return (
                    <div
                      key={g.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 14px',
                        background: '#f9f9f9',
                        borderRadius: 10,
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <strong style={{ fontSize: 14, color: '#1a1c2c' }}>
                            {g.guardianName}
                          </strong>
                          <span
                            style={{
                              fontSize: 11,
                              color: '#888',
                              background: '#eee',
                              padding: '1px 8px',
                              borderRadius: 10,
                            }}
                          >
                            {g.relationship === 'PARENT'
                              ? 'Padre/Madre'
                              : g.relationship === 'GRANDPARENT'
                                ? 'Abuelo/a'
                                : g.relationship === 'SIBLING'
                                  ? 'Hermano/a'
                                  : 'Otro'}
                          </span>
                          {expired && (
                            <span
                              style={{
                                fontSize: 11,
                                color: '#c00',
                                background: '#ffe5e5',
                                padding: '1px 8px',
                                borderRadius: 10,
                              }}
                            >
                              Vencido
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                          {g.guardianDocument && <>Doc: {g.guardianDocument} · </>}
                          {g.validUntil ? (
                            <>
                              Vigencia: {fmtDate(g.validUntil)}
                              {expired ? ' (vencido)' : ''}
                            </>
                          ) : (
                            'Sin vigencia'
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => toggleAuth(g)}
                          style={{
                            background: g.isAuthorized ? '#4caf50' : '#999',
                            color: '#fff',
                            padding: '5px 10px',
                            borderRadius: 6,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {g.isAuthorized ? 'Autorizado' : 'Bloqueado'}
                        </button>
                        <button
                          onClick={() => deleteGuardian(g.id)}
                          style={{
                            background: 'none',
                            border: '1px solid #ddd',
                            color: '#c00',
                            padding: '5px 10px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 11,
                          }}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {!selectedStudent && !loading && (
        <div
          style={{
            textAlign: 'center',
            padding: 40,
            color: '#bbb',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <p style={{ fontSize: 14 }}>Seleccioná un alumno para ver sus tutores autorizados</p>
        </div>
      )}
    </div>
  );
}
